import { PasseriniAPI } from './apiService';

export type WhatsAppEventType =
  | 'escrituras.recordatorio_firma_dia'
  | 'escrituras.mensaje_rapido_grupo'
  | 'escrituras.envio_escritura'
  | 'escrituras.envio_escritura_inscripta'
  | 'escrituras.envio_presupuesto'
  | 'contabilidad.envio_documento'
  | 'contabilidad.recordatorio_pago'
  | 'otros_tramites.documentacion_lista_retiro'
  | 'otros_tramites.envio_archivo'
  | 'otros_tramites.envio_factura';

export type DispatchStatus = 'queued' | 'processing' | 'completed' | 'completed_with_errors' | 'failed';
export type DispatchRecipientStatus = 'queued' | 'sent' | 'failed' | 'skipped_duplicate' | 'invalid_phone';

interface PayloadBase {
  event_type: WhatsAppEventType;
  context: Record<string, unknown>;
}

export type PreviewPayload =
  | (PayloadBase & { template_id: number; message_body?: never })
  | (PayloadBase & { message_body: string; template_id?: number });

export interface PreviewResponse {
  rendered_message: string;
  resolved_variables: Record<string, unknown>;
  missing_variables: string[];
}

interface CreateDispatchPayloadBase extends PayloadBase {
  phones?: string[];
  recipients?: Array<{
    phone: string;
    name?: string;
  }>;
  client_idempotency_key?: string;
}

export type CreateDispatchPayload =
  | (CreateDispatchPayloadBase & { template_id: number; message_body?: never })
  | (CreateDispatchPayloadBase & { message_body: string; template_id?: number });

export interface CreateDispatchResponse {
  dispatch_id: number;
  status: DispatchStatus;
  totals: {
    total: number;
    sent: number;
    failed: number;
    invalid: number;
    skipped_duplicate: number;
  };
  created_at: string;
}

export interface DispatchListItem {
  id: number;
  event_type: WhatsAppEventType;
  provider: string;
  status: DispatchStatus;
  template: { id: number; name: string } | null;
  requested_by: { id: number; name: string; email: string } | null;
  totals: {
    total: number;
    sent: number;
    failed: number;
    invalid: number;
    skipped_duplicate: number;
  };
  recipients_count: number;
  created_at: string;
  updated_at: string;
}

export interface DispatchRecipientDetail {
  id: number;
  recipient_name?: string | null;
  phone_original: string;
  phone_e164: string;
  status: DispatchRecipientStatus;
  rendered_message?: string | null;
  provider_message_id?: string | null;
  provider_response?: Record<string, unknown> | null;
  error_code?: string | null;
  error_message?: string | null;
  attempts: number;
  created_at: string;
  updated_at: string;
}

export interface DispatchDetail extends Omit<DispatchListItem, 'recipients_count'> {
  context: Record<string, unknown> | null;
  recipients: DispatchRecipientDetail[];
}

export interface PaginatedDispatches {
  data: DispatchListItem[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  links: Array<{
    url: string | null;
    label: string;
    active: boolean;
  }>;
}

export interface ListDispatchesParams {
  status?: DispatchStatus;
  event_type?: WhatsAppEventType;
  search?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
}

export interface RetryDispatchResponse {
  dispatch_id: number;
  status: DispatchStatus;
  totals: {
    total: number;
    sent: number;
    failed: number;
    invalid: number;
    skipped_duplicate: number;
  };
  created_at: string;
  retry_of_dispatch_id: number;
}

export interface TemplateItem {
  id: number;
  event_type: WhatsAppEventType;
  name: string;
  body_template: string;
  is_active: boolean;
  version: number;
}

export interface PaginatedTemplates {
  data: TemplateItem[];
  current_page: number;
  last_page: number;
  total: number;
}

export const whatsappService = {
  async listDispatches(params: ListDispatchesParams = {}): Promise<PaginatedDispatches> {
    const response = await PasseriniAPI.get('/whatsapp/dispatches', { params });
    return response.data;
  },

  async previewMessage(payload: PreviewPayload): Promise<PreviewResponse> {
    const response = await PasseriniAPI.post('/whatsapp/preview', payload);
    return response.data;
  },

  async createDispatch(payload: CreateDispatchPayload): Promise<CreateDispatchResponse> {
    const response = await PasseriniAPI.post('/whatsapp/dispatches', payload);
    return response.data;
  },

  async getDispatch(id: number): Promise<DispatchDetail> {
    const response = await PasseriniAPI.get(`/whatsapp/dispatches/${id}`);
    return response.data;
  },

  async retryDispatch(dispatchId: number, recipientIds: number[]): Promise<RetryDispatchResponse> {
    const response = await PasseriniAPI.post(`/whatsapp/dispatches/${dispatchId}/retry`, {
      recipient_ids: recipientIds,
    });
    return response.data;
  },

  async getTemplates(params: { event_type?: WhatsAppEventType; is_active?: boolean; page?: number } = {}): Promise<PaginatedTemplates> {
    const response = await PasseriniAPI.get('/whatsapp/templates', { params });
    return response.data;
  },
};

export default whatsappService;
